import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { DraggableImageWrapper } from "src/components/atoms/DraggableImageWrapper";
import { useMaxView, useViewport } from "src/contexts";
import { NavStep, PreviewCard } from "src/pages/configurator/components";
import { FloorPlan, IconButton, ImagePlaceholder, InsideMapsIFrame, TooltipV2 } from "~components";
import { Css, Palette } from "~generated/css";
import { Maybe } from "~generated/graphqlTypes";
import { defaultTestId, isDefined, useContainerBreakpoints, useTestIds } from "~utils";

type ImageProps = {
  kind: "image";
  imageUrl: Maybe<string>;
  id?: string;
  isReversed?: boolean;
  name?: string;
  description?: Maybe<string>;
  previewCardName?: string;
  bgColor?: Palette;
  // Initial zoom scale needs to be higher for renderings
  isRendering?: boolean;
};

type FloorPlanProps = {
  kind: "floorPlan";
  imageUrl: Maybe<string>;
  id: string;
  isReversed?: boolean;
  name?: string;
  description?: Maybe<string>;
  previewCardName?: string;
};

type InsideMapsProps = {
  kind: "insideMaps";
  roomLocationId: string | undefined;
  previewCardName?: string;
};

export type ImageDisplayImageProps = ImageProps | FloorPlanProps | InsideMapsProps;

// TODO: SC-76104, add an optional callback to handle full screen expansion.
// Currently just blowing up `left` to a maxView modal in our StepWrapper component only works when we're displaying one image. If we're showing multiple images it only ever shows the first.
// Lucky for us the `pin` scroll button only shows up on mobile and is absolutely positioned so we should be able to position it and make the move pretty easily.
// I'm thinking of using a callback instead of adding another use of ModalWrapper here since their is already a ton going on inside this component but that might actually be the simplest
type ImageDisplayProps = {
  images: ImageDisplayImageProps[];
  // NOTE: contrast is essentially interchangeable `darkMode` but we have no concept of that design in our app yet;
  contrast?: boolean;
  selectedOptionCodes?: string[];
  imageIndex?: number;
  isReversed?: boolean;
  onClick?: (index: number) => void;
};

const HORIZONTAL_BREAKPOINT = 782; // mainly for preview cards;
export function ImageDisplay(props: ImageDisplayProps) {
  const { images, contrast = false, selectedOptionCodes, imageIndex, isReversed = false, onClick } = props;
  const tid = useTestIds(props, "image-display");
  const { isMobile } = useViewport();
  const { isMaxViewOpen, openMaxViewModal } = useMaxView();
  const [imgExpanded, setImgExpanded] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  // allow setting of index from parent component
  const [imgDisplayIdx, setImgDisplayIdx] = useState(isDefined(imageIndex) ? imageIndex : 0);
  const { ref: mainContainerRef } = useContainerBreakpoints<HTMLDivElement>({
    reduced: [0, HORIZONTAL_BREAKPOINT],
  });

  useEffect(() => {
    // set based on if provided value changes from parent component
    if (imageIndex) {
      setImgDisplayIdx(imageIndex);
      return;
    }
    setImgDisplayIdx(0);
    // adding images to dependency array to reset index when using
    // toggle to switch between image and floor plan since they may
    // have different number of images
  }, [imageIndex, images]);

  if (images.length === 0) {
    return <ImagePlaceholder />;
  }
  if (images.length === 1) {
    const image = images[0];

    return (
      <div
        ref={mainContainerRef}
        css={{
          ...Css.df.fg1.h100.oh.relative.$,
          // Change the background color when in contrast mode
          ...Css.bgOffWhite.if(contrast).bgGray800.$,
        }}
        {...tid}
      >
        {/* [SC-81423] Expanding inside maps renders a 2nd iframe which is crashing on mobile. Disabling for now while we work on a real fix. */}
        {isMobile && !isInsideMapsImage(image) && (
          <div css={Css.absolute.top2.right8.z1.df.gap2.$}>
            <IconButton
              icon="expand-small"
              color="primary"
              size="medium"
              shape="square"
              onClick={() =>
                openMaxViewModal(
                  <MainContent
                    image={image}
                    isReversed={isReversed}
                    selectedOptionCodes={selectedOptionCodes}
                    imageLoading={imageLoading}
                    setImageLoading={setImageLoading}
                    imgExpanded
                    hideAlternateCameraToggle
                  />,
                )
              }
              {...tid.expand_button}
            />
          </div>
        )}
        {/* Image Container */}
        <div css={Css.df.fdc.w100.$}>
          <div css={Css.add("transition", "0.1s").relative.z0.df.w100.oh.ar("5 / 3").fg1.$}>
            <MainContent
              image={image}
              isReversed={isReversed}
              selectedOptionCodes={selectedOptionCodes}
              imageLoading={imageLoading}
              setImageLoading={setImageLoading}
            />
          </div>
          {/* Note: Only our plan step uses this */}
          {/* 9% is sweetspot for padding. Overall looks better than trying to enforce a width here with mxa */}
          {isStandardImage(image) && image.name && image.description && (
            <div
              css={Css.df.fdc.jcc.maxw100.px("9%").bgOffWhite.mya.pt2.pb3.if(!!image.bgColor).bgColor(image.bgColor).$}
            >
              <h6 css={Css.t18.$}>{image.name}</h6>
              <div css={Css.mt1.t12.gray700.$}>{image.description}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentImage = images[imgDisplayIdx];

  return (
    <div
      ref={mainContainerRef}
      css={{
        ...Css.df.fdc.h100.w100.relative.$,
        // Change the background color when in contrast mode or on mobile
        ...Css.bgOffWhite.ifMobile.bgWhite.$,
        ...Css.if(contrast).bgGray800.$,
      }}
      {...tid}
    >
      {/* On mobile this triggers a full screen takeover of the image.  */}
      {/* [SC-81423] Expanding inside maps renders a 2nd iframe which is crashing on mobile. Disabling for now while we work on a real fix. */}
      {isMobile && !isInsideMapsImage(currentImage) && (
        <div css={Css.absolute.top2.right8.z1.$}>
          <IconButton
            icon="expand-small"
            color="primary"
            size="medium"
            shape="square"
            onClick={() =>
              openMaxViewModal(
                <MainContent
                  image={currentImage}
                  isReversed={isReversed}
                  selectedOptionCodes={selectedOptionCodes}
                  imgExpanded
                  // If we're in mobile view and showing multiple insideMap iframes (which we must be just by being w/in this block), hide the alternate camera toggle
                  hideAlternateCameraToggle
                />,
              )
            }
            {...tid.expand_button}
          />
        </div>
      )}
      {!isMobile && (
        <div css={Css.absolute.top3.right4.z1.$} id="expand-collapse">
          <IconButton
            icon={imgExpanded ? "collapse" : "expand"}
            color="primary"
            size="medium"
            shape="square"
            onClick={() => setImgExpanded(!imgExpanded)}
            {...tid.expand_button}
          />
          <TooltipV2 anchorSelect="#expand-collapse" cssOverrides={Css.px1.py("4px").bgGray800.$}>
            <div css={Css.white.z1.$}>{imgExpanded ? "Collapse" : "Expand"}</div>
          </TooltipV2>
        </div>
      )}
      {/* Image Container */}
      <div css={Css.relative.oh.df.w100.ar("5 / 3").if(imgExpanded).h100.else.fg1.$}>
        {currentImage ? (
          <MainContent
            image={currentImage}
            isReversed={isReversed}
            selectedOptionCodes={selectedOptionCodes}
            imgExpanded={imgExpanded}
            // If we're in mobile view and showing multiple insideMap iframes (which we must be just by being w/in this block), hide the alternate camera toggle
            hideAlternateCameraToggle={isMobile}
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      {/* Preview Cards Container */}
      <div
        id="preview-card-container"
        css={{
          ...Css.df.w100.fs0.p2.px1.oa.add("transition", "height 0.3s ease-in-out, padding 0.3s ease-in-out").$,
          ...Css.ifMobile.py0.hPx(52).oahs.$,
          // Transition and setting height/padding to 0 when imgExpanded or isMaxViewOpen is true
          // prevents the image preview from jumping to a larger size while our modal opens in modal
          ...Css.if(imgExpanded || isMaxViewOpen).important.h0.p0.$,
        }}
      >
        <div css={Css.wminc.df.gap1.aic.jcc.mxa.$}>
          {images.map((image, i) => {
            const key = image.previewCardName ?? `preview-card-${i}`;
            if (isMobile) {
              return (
                <NavStep
                  key={key}
                  menuTitle={image.previewCardName ?? ""}
                  isActive={i === imgDisplayIdx}
                  onClick={() => {
                    setImgDisplayIdx(i);
                    onClick?.(i);
                  }}
                />
              );
            }
            return (
              <PreviewCard
                key={key}
                image={image}
                isActive={i === imgDisplayIdx}
                onClick={() => {
                  setImgDisplayIdx(i);
                  onClick?.(i);
                }}
                isReversed={isReversed}
                selectedOptionCodes={selectedOptionCodes}
                {...tid[`previewCard_${defaultTestId(key)}`]}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Main slice point for rendering our different media types */
function MainContent({
  image,
  isReversed,
  selectedOptionCodes,
  imgExpanded,
  imageLoading,
  setImageLoading,
  hideAlternateCameraToggle,
}: {
  image: ImageDisplayImageProps;
  isReversed: boolean;
  selectedOptionCodes?: string[];
  imgExpanded?: boolean;
  imageLoading?: boolean;
  setImageLoading?: (loading: boolean) => void;
  hideAlternateCameraToggle?: boolean;
}) {
  if (isStandardImage(image)) {
    return (
      <ImageDisplayView
        image={image}
        isReversed={isReversed}
        imageLoading={imageLoading || false}
        setImageLoading={setImageLoading || (() => {})}
        imgExpanded={imgExpanded}
      />
    );
  }

  if (isFloorPlanImage(image) && isDefined(image.imageUrl)) {
    return (
      <FloorPlanDisplayView
        image={image}
        isReversed={isReversed}
        selectedOptionCodes={selectedOptionCodes}
        imgExpanded={imgExpanded}
      />
    );
  }

  if (isInsideMapsImage(image)) {
    return (
      <InsideMapsIFrame
        iframeId="summary-insidemaps-iframe"
        roomLocationId={image.roomLocationId}
        isPreview={false}
        hideAlternateCameraToggle={hideAlternateCameraToggle}
      />
    );
  }

  return <ImagePlaceholder />;
}

function ImageDisplayView({
  image,
  isReversed,
  imageLoading,
  setImageLoading,
  imgExpanded,
}: {
  image: ImageProps;
  isReversed: boolean;
  imageLoading: boolean;
  setImageLoading: (loading: boolean) => void;
  imgExpanded?: boolean;
}) {
  if (!image.imageUrl) return <ImagePlaceholder text={image.name} />;

  if (image.imageUrl.includes(".svg")) {
    return <ImagePlaceholder src={image.imageUrl} bgColor={image.bgColor} size={{ height: 283, width: 403 }} />;
  }

  return (
    <DraggableImageWrapper draggable={!!imgExpanded} initialScale={image.isRendering ? 3 : 1}>
      <motion.img
        onLoad={() => setImageLoading(false)}
        css={{
          // Catches landscape views on mobile to allow users to pinch and zoom as needed
          ...Css.w100.h100.objectCover.ifTablet.objectContain.$,
          ...(imgExpanded ? Css.objectContain.$ : Css.$),
          ...Css.if(isReversed || !!image.isReversed).add("transform", "scaleX(-1)").$,
        }}
        src={image.imageUrl || ""}
        alt={image.name || ""}
      />
      {/* Image mask */}
      <motion.div
        initial={{ opacity: 1 }}
        css={Css.absolute.bgLightBlue.z1.w100.h100.$}
        animate={
          !imageLoading && {
            opacity: 0,
            transition: { duration: 0.4 },
            transitionEnd: { display: "none", zIndex: -1 },
          }
        }
      />
    </DraggableImageWrapper>
  );
}

function FloorPlanDisplayView({
  image,
  isReversed,
  selectedOptionCodes,
  imgExpanded,
}: {
  image: FloorPlanProps;
  isReversed: boolean;
  selectedOptionCodes?: string[];
  imgExpanded?: boolean;
}) {
  if (!image.imageUrl) return <ImagePlaceholder text={image.name} />;

  return (
    <DraggableImageWrapper draggable={!!imgExpanded}>
      <div css={Css.bgOffWhite.w100.$}>
        <FloorPlan
          id={image.id?.replaceAll(":", "") ?? ""}
          key={image.id}
          src={image.imageUrl || ""}
          xss={Css.h100.ma.if(!imgExpanded).pt8.$}
          highlightHexColor="#C59877"
          maskHexColor="#F6F6F0"
          selectedOptionCodes={selectedOptionCodes}
          isReversed={isReversed || image.isReversed}
        />
      </div>
    </DraggableImageWrapper>
  );
}

function isInsideMapsImage(image: ImageDisplayImageProps): image is InsideMapsProps {
  return image.kind === "insideMaps";
}

function isFloorPlanImage(image: ImageDisplayImageProps): image is FloorPlanProps {
  return image.kind === "floorPlan";
}

function isStandardImage(image: ImageDisplayImageProps): image is ImageProps {
  return image.kind === "image";
}
